import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { leaveSchool } from "@/services/studentService";
import { todayISO } from "@/utils/format";
import type { Enrollment, Student } from "@/types";

const REASONS = ["Déménagement", "Changement d'école", "Abandon", "Exclusion", "Raisons financières", "Raisons de santé"];
const OTHER = "__autre__";

interface Props {
  /** Élève et inscription de l'année qui se termine par ce départ (null : fermé). */
  target: { student: Student; enrollment: Enrollment } | null;
  onClose: () => void;
  onDone: () => void;
}

/** Déclarer qu'un élève a quitté l'école : il sort des listes de classe, son dossier reste consultable. */
export default function LeaveSchoolModal({ target, onClose, onDone }: Props) {
  const { notify } = useToast();
  const [date, setDate] = useState(todayISO());
  const [choice, setChoice] = useState(REASONS[0]);
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setDate(todayISO());
    setChoice(REASONS[0]);
    setOther("");
  }, [target]);

  const reason = choice === OTHER ? other.trim() : choice;

  const submit = async () => {
    if (!target) return;
    if (!reason) return notify("Précisez le motif du départ.", "error");
    setBusy(true);
    try {
      await leaveSchool(target.enrollment.id, date, reason);
      notify(`${target.student.firstName} ${target.student.lastName} est enregistré(e) comme ayant quitté l'école.`);
      onDone();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title="Déclarer un départ"
      subtitle={target ? `${target.student.firstName} ${target.student.lastName} — ${target.student.matricule}` : undefined}
      icon="ri-logout-box-r-line"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button variant="danger" icon="ri-logout-box-r-line" onClick={submit} disabled={busy || !reason}>
            {busy ? "Enregistrement..." : "Confirmer le départ"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Date du départ"
            type="date"
            value={date}
            min={target?.enrollment.enrollmentDate}
            max={todayISO()}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select label="Motif" value={choice} onChange={(e) => setChoice(e.target.value)}>
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            <option value={OTHER}>Autre motif…</option>
          </Select>
        </div>
        {choice === OTHER && (
          <Input label="Motif" value={other} maxLength={255} placeholder="Précisez le motif" onChange={(e) => setOther(e.target.value)} />
        )}
        <ul className="space-y-1.5 rounded-md border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-700">
          <li>L'élève n'apparaîtra plus dans les listes de classe, les notes, les bulletins ni les cartes scolaires.</li>
          <li>Son dossier, son historique et ses paiements restent consultables.</li>
          <li>Ce qu'il doit déjà reste dû ; les tranches dont l'échéance tombe après son départ ne sont plus réclamées.</li>
          <li>Un administrateur peut le réintégrer en cas d'erreur.</li>
        </ul>
      </div>
    </Modal>
  );
}
